import { useCallback, useEffect, useState } from "react";
import type {
  AgencyWebsiteOwnershipViewV1,
  WebsiteOwnershipChecklistItemState,
} from "@enrein/shared-contracts/contracts/website-ownership-record";
import type { StudioApiClient } from "./api.js";

export function WebsiteOwnershipRecordPanel({
  api,
  projectId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string | null;
}) {
  const [view, setView] = useState<AgencyWebsiteOwnershipViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      setView(await api.getWebsiteOwnershipRecord(projectId));
    } catch (err) {
      setView(null);
      const msg = err instanceof Error ? err.message : String(err);
      if (
        msg.includes("website_ownership_record_not_found") ||
        /^not found$/i.test(msg.trim()) ||
        msg.includes("studio_api_error_404")
      ) {
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateItem = async (itemId: string, state: WebsiteOwnershipChecklistItemState) => {
    if (!projectId || !view) return;
    setError(null);
    setMessage(null);
    try {
      const updated = await api.updateWebsiteOwnershipRecord(projectId, {
        schemaVersion: "website-ownership-checklist-update.v1.0",
        items: [{ itemId, state }],
      });
      setView(updated);
      setMessage("Checklist updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!projectId) {
    return <p className="empty-state">Select a project to manage website ownership checklist.</p>;
  }
  if (busy && !view) return <p className="loading-panel">Loading ownership record…</p>;
  if (error) return <p className="error">{error}</p>;
  if (!view) {
    return (
      <p className="empty-state">
        Ownership record will appear after Departure Kit export seeds checklist state.
      </p>
    );
  }

  return (
    <div className="ownership-record-panel">
      <p className="eyebrow">Website ownership record</p>
      <h3>{view.riskScoreLabel} heuristic gap indicator</h3>
      <p>{view.riskScoreExplanation}</p>
      <p className="muted">Not legal proof of ownership — handoff checklist state only.</p>
      <ul className="ownership-checklist">
        {view.checklist.map((item) => (
          <li key={item.itemId}>
            <span>{item.label}</span>
            <select
              value={item.state}
              onChange={(event) =>
                void updateItem(item.itemId, event.target.value as WebsiteOwnershipChecklistItemState)
              }
              aria-label={`State for ${item.label}`}
            >
              <option value="unchecked">Not documented</option>
              <option value="noted">Noted</option>
              <option value="confirmed">Documented</option>
            </select>
          </li>
        ))}
      </ul>
      {message ? <p className="muted">{message}</p> : null}
      <details>
        <summary>Disclaimers</summary>
        <ul>
          {view.disclaimers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
